import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Mic, Loader2, Volume2, Check } from "lucide-react";
import { API, localDate } from "@/lib/api";
import { toast } from "sonner";

const clean = (t) => (t || "").replace(/\*\*(.*?)\*\*/g, "$1").replace(/^#+\s*/gm, "").replace(/\s[—–]\s/g, ". ").replace(/[—–]/g, ", ");

const pickMime = () => {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/ogg;codecs=opus"]) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return "";
};

const SILENCE_MS = 1300;   // hang time after you stop talking
const MIN_SPEECH_MS = 400; // ignore coughs / clicks
const MAX_TURN_MS = 30000; // safety cap per turn
const SPEECH_ON = 0.018;   // RMS to count as talking
const BARGE = {            // how easily talking over the coach interrupts it
  sensitive: { on: 0.045, ms: 250 },
  normal: { on: 0.06, ms: 320 },
  quiet: { on: 0.11, ms: 550 },
};
const token = () => localStorage.getItem("ihmj_token");
const SR = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
const SENT_RE = /[^\n]*?[.!?]+["')\]]*[ \t]+|[^\n]*\n+/y;

// Pull complete sentences off the front of the buffer. Returns [sentences, rest].
const takeSentences = (buf) => {
  const out = [];
  SENT_RE.lastIndex = 0;
  let m, end = 0, carry = "";
  while ((m = SENT_RE.exec(buf)) && m[0].length) {
    const t = (carry + " " + m[0]).trim();
    if (t.length < 4) { carry = t; continue; }
    out.push(t); carry = ""; end = SENT_RE.lastIndex;
  }
  return [out, buf.slice(end)];
};

const VOICES = [
  { id: "onyx", label: "Deep", sub: "Onyx" },
  { id: "coral", label: "Warm", sub: "Coral" },
];

// phase: connecting | listening | thinking | speaking | error
const SPOKEN_CAP = 350;
const WORK_CHARS = 700;
const READ_RE = /\bread\b/i;

export default function VoiceConversation({ onClose, onLimit }) {
  const [phase, setPhase] = useState("connecting");
  const [level, setLevel] = useState(0);
  const [turns, setTurns] = useState([]);
  const [live, setLive] = useState("");
  const [err, setErr] = useState("");
  const [heard, setHeard] = useState(false);
  const [voice, setVoice] = useState(() => localStorage.getItem("ihmj_coach_voice") || "onyx");

  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const recRef = useRef(null);
  const rafRef = useRef(null);
  const bargeRafRef = useRef(null);
  const abortRef = useRef(null);
  const playerRef = useRef(null);
  const bargeRef = useRef(BARGE[localStorage.getItem("ihmj_barge_sensitivity")] || BARGE.normal);
  const aliveRef = useRef(true);
  const phaseRef = useRef("connecting");
  const voiceRef = useRef(voice);
  const turnsRef = useRef([]);
  const scrollRef = useRef(null);
  const turnRef = useRef(null);
  const srRef = useRef(null);
  const [latency, setLatency] = useState(null);
  const bootIdRef = useRef(0);

  const setP = (p) => { phaseRef.current = p; setPhase(p); };

  useEffect(() => { voiceRef.current = voice; localStorage.setItem("ihmj_coach_voice", voice); }, [voice]);
  useEffect(() => { turnsRef.current = turns; }, [turns]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9, behavior: "smooth" }); }, [turns, live, phase]);

  useEffect(() => {
    aliveRef.current = true;
    boot(++bootIdRef.current);
    return () => { bootIdRef.current++; aliveRef.current = false; teardown(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teardown = () => {
    cancelAnimationFrame(rafRef.current);
    cancelAnimationFrame(bargeRafRef.current);
    try { abortRef.current && abortRef.current.abort(); } catch {}
    if (turnRef.current) { turnRef.current.cancelled = true; try { turnRef.current.ctrl.abort(); } catch {} }
    try { srRef.current?.rec.abort(); } catch {}
    try { recRef.current && recRef.current.state !== "inactive" && recRef.current.stop(); } catch {}
    try { playerRef.current && playerRef.current.pause(); } catch {}
    if (playerRef.current?.src) { try { URL.revokeObjectURL(playerRef.current.src); } catch {} }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    try { ctxRef.current && ctxRef.current.close(); } catch {}
  };

  // fetch with an abort timeout; the controller is also aborted on unmount
  const runFetch = (url, opts, ms) => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const to = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { ...opts, signal: ctrl.signal }).finally(() => clearTimeout(to));
  };

  const boot = async (id) => {
    const stale = () => id !== bootIdRef.current || !aliveRef.current;
    const mime = pickMime();
    if (mime === null || !navigator.mediaDevices?.getUserMedia) {
      setErr("This browser can't record audio. Try Chrome or Safari on your phone.");
      setP("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      if (stale()) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      await ctx.resume().catch(() => {});
      if (stale()) { stream.getTracks().forEach((t) => t.stop()); ctx.close().catch(() => {}); return; }
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      ctxRef.current = ctx;
      analyserRef.current = analyser;
      await calibrateNoise();
      if (stale()) return;
      greetOrListen();
    } catch {
      setErr("Microphone blocked. Allow mic access, then tap the mic to try again.");
      setP("error");
    }
  };

  // Speak the welcome-back line for the active project, then hand the mic over.
  const greetOrListen = async () => {
    const w = await fetch(`${API}/coach/welcome`, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
    if (!aliveRef.current) return;
    if (!w.welcome) { listen(); return; }
    const turn = { t0: performance.now(), audio: [], cancelled: false, streamDone: true, wake: null, ctrl: new AbortController(), firstAudio: false };
    turnRef.current = turn;
    setTurns((t) => [...t, { role: "assistant", content: w.welcome }]);
    setP("thinking");
    queueSpeech(w.welcome, turn);
    playQueue(turn);
  };

  // Sample the room for a beat and, if it's noisy, make it harder to interrupt by accident.
  const calibrateNoise = async () => {
    const samples = [];
    for (let i = 0; i < 30; i++) {
      samples.push(rms());
      await new Promise((r) => setTimeout(r, 20));
      if (!aliveRef.current) return;
    }
    const avg = samples.reduce((a, b) => a + b, 0) / (samples.length || 1);
    const level = avg > 0.05 ? "quiet" : avg > 0.02 ? "normal" : "sensitive";
    const order = { sensitive: 0, normal: 1, quiet: 2 };
    const saved = localStorage.getItem("ihmj_barge_sensitivity") || "normal";
    const chosen = order[level] > order[saved] ? level : saved;
    bargeRef.current = BARGE[chosen] || BARGE.normal;
    if (order[level] > order[saved]) {
      toast(level === "quiet"
        ? "Noisy here. Made it harder to cut in by accident."
        : "Tuned how easily you can cut in to match your background.");
    }
  };

  const rms = () => {
    const analyser = analyserRef.current;
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
    return Math.sqrt(sum / buf.length);
  };

  const listen = () => {
    if (!aliveRef.current) return;
    const stream = streamRef.current;
    if (!stream || !analyserRef.current) return;
    cancelAnimationFrame(rafRef.current);
    setLive("");
    setHeard(false);
    setP("listening");
    const prev = recRef.current;
    if (prev && prev.state !== "inactive") { prev.onstop = null; try { prev.stop(); } catch {} }
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    recRef.current = rec;
    const chunks = [];
    rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
    rec.onstop = () => processTurn(rec.mimeType || mime || "audio/webm", chunks);
    rec.start(200);
    startSR();

    const startAt = performance.now();
    let speechMs = 0;
    let lastVoice = 0;
    let hadSpeech = false;

    const tick = () => {
      if (phaseRef.current !== "listening") return;
      const r = rms();
      setLevel(Math.min(1, r * 6));
      const now = performance.now();
      if (r > SPEECH_ON) {
        if (!hadSpeech) setHeard(true);
        hadSpeech = true; lastVoice = now; speechMs += 16;
      }
      const stop = () => { try { rec.state !== "inactive" && rec.stop(); } catch {} };
      if (hadSpeech && speechMs > MIN_SPEECH_MS && now - lastVoice > SILENCE_MS) { stop(); return; }
      if (now - startAt > MAX_TURN_MS) { stop(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  // Live browser transcription (instant). Whisper stays as the fallback.
  const startSR = () => {
    srRef.current = null;
    if (!SR) return;
    try {
      const r = new SR();
      r.lang = "en-US"; r.continuous = true; r.interimResults = true;
      const st = { rec: r, finals: "", interim: "", failed: false };
      st.ended = new Promise((res) => { r.onend = res; });
      r.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) st.finals += " " + e.results[i][0].transcript; else interim += e.results[i][0].transcript;
        }
        st.interim = interim;
      };
      r.onerror = () => { st.failed = true; };
      r.start();
      srRef.current = st;
    } catch { srRef.current = null; }
  };

  const stopSR = async () => {
    const st = srRef.current;
    srRef.current = null;
    if (!st) return "";
    try { st.rec.stop(); } catch {}
    await Promise.race([st.ended, new Promise((r) => setTimeout(r, 700))]);
    return st.failed ? "" : `${st.finals} ${st.interim}`.trim();
  };

  const transcribe = async (blob, mimeType) => {
    const fd = new FormData();
    fd.append("audio", blob, `voice.${mimeType.includes("mp4") ? "mp4" : "webm"}`);
    const tr = await runFetch(`${API}/coach/transcribe`, { method: "POST", headers: { Authorization: `Bearer ${token()}` }, body: fd }, 25000);
    const td = await tr.json().catch(() => ({}));
    if (!tr.ok) { if (tr.status === 400) return ""; throw new Error(td.detail || "Couldn't hear that."); }
    return (td.text || "").trim();
  };

  const ttsFetch = (text, turn) => Promise.race([
    fetch(`${API}/coach/speak`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
      body: JSON.stringify({ text, voice: voiceRef.current }),
      signal: turn.ctrl.signal,
    }).then((r) => (r.ok ? r.arrayBuffer() : null)).catch(() => null),
    new Promise((r) => setTimeout(() => r(null), 20000)),
  ]);

  const queueSpeech = (text, turn) => {
    if (!text.trim() || turn.cancelled) return;
    turn.audio.push(ttsFetch(text, turn));
    turn.wake?.();
  };

  const processTurn = async (mimeType, chunks) => {
    if (!aliveRef.current) return;
    cancelAnimationFrame(rafRef.current);
    setLevel(0);
    const t0 = performance.now();
    setP("thinking");
    const blob = new Blob(chunks, { type: mimeType });
    const quick = await stopSR();
    if (!quick && blob.size < 1200) { if (aliveRef.current) listen(); return; }
    const turn = { t0, audio: [], cancelled: false, streamDone: false, wake: null, ctrl: new AbortController(), firstAudio: false };
    turnRef.current = turn;
    try {
      const said = quick || await transcribe(blob, mimeType);
      if (!said) { if (aliveRef.current) listen(); return; }
      if (!aliveRef.current || turn.cancelled) return;
      setTurns((t) => [...t, { role: "user", content: said }]);

      const cr = await runFetch(`${API}/coach`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}`, "X-Local-Date": localDate() },
        body: JSON.stringify({ message: said, voice: true }),
      }, 45000);
      if (!cr.ok || !cr.body) {
        const d = await cr.json().catch(() => ({}));
        const msg = typeof d.detail === "string" ? d.detail : "Coach unavailable.";
        if (cr.status === 402) { turn.cancelled = true; setP("idle"); onLimit?.(msg); return; }
        throw new Error(msg);
      }
      playQueue(turn);
      const reader = cr.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      let pending = "";
      let spoken = 0;
      let batch = [];
      let spokenChars = 0;
      let reviewed = false;
      const held = [];
      const readAloud = READ_RE.test(said);
      const say = (t) => {
        if (!t.trim()) return;
        if (!readAloud && spokenChars >= SPOKEN_CAP) { held.push(t); return; }
        spokenChars += t.length;
        queueSpeech(t, turn);
      };
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        let piece = dec.decode(value, { stream: true });
        acc += piece;
        if (acc.includes("\u001e")) {
          if (aliveRef.current && !turn.cancelled) setLive(acc.split("\u001e")[1] || acc.split("\u001e")[0]);
          if (reviewed) continue;
          reviewed = true;
          piece = piece.split("\u001e")[0];
        } else if (aliveRef.current && !turn.cancelled) setLive(acc);
        pending += piece;
        const [sentences, rest] = takeSentences(pending);
        pending = rest;
        for (const sn of sentences) {
          if (spoken === 0) { say(sn); spoken++; continue; }
          batch.push(sn);
          if (batch.length >= 2 || batch.join(" ").length >= 140) { say(batch.join(" ")); spoken++; batch = []; }
        }
      }
      say([...batch, pending].join(" "));
      if (held.length) queueSpeech(acc.length >= WORK_CHARS ? "The full version is on your screen and saved in My Work." : held.join(" "), turn);
      turn.streamDone = true;
      turn.wake?.();
      if (!aliveRef.current) return;
      setTurns((t) => [...t, { role: "assistant", content: acc.includes("\u001e") ? (acc.split("\u001e")[1] || acc.split("\u001e")[0]) : acc }]);
      if (!turn.cancelled) setLive("");
    } catch (e) {
      if (!aliveRef.current || turn.cancelled) return;
      turn.cancelled = true;
      toast.error(e.name === "AbortError" ? "That took too long. Let's try again." : (e.message || "Something went wrong."));
      listen();
    }
  };

  // Play each chunk the moment its audio is ready, in order, while later chunks are still generating.
  const playQueue = async (turn) => {
    let i = 0;
    while (aliveRef.current && !turn.cancelled) {
      if (i >= turn.audio.length) {
        if (turn.streamDone) break;
        await new Promise((r) => { turn.wake = r; });
        continue;
      }
      const buf = await turn.audio[i++];
      if (!buf || turn.cancelled || !aliveRef.current) continue;
      await playBuf(buf, turn);
    }
    if (aliveRef.current && !turn.cancelled && turnRef.current === turn) listen();
  };

  const playBuf = (buf, turn) => new Promise((resolve) => {
    const url = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
    const audio = new Audio(url);
    playerRef.current = audio;
    let settled = false;
    const done = () => { if (settled) return; settled = true; URL.revokeObjectURL(url); turn.stopAudio = null; resolve(); };
    turn.stopAudio = () => { try { audio.pause(); } catch {} done(); };
    audio.onended = done;
    audio.onerror = done;
    audio.onplaying = () => {
      if (turn.firstAudio) return;
      turn.firstAudio = true;
      const ms = Math.round(performance.now() - turn.t0);
      setLatency(ms);
      console.info(`[voice] first audio after ${ms} ms`);
      setP("speaking");
      watchBargeIn();
    };
    audio.play().catch(done);
  });

  // While the coach talks, listen for the user cutting in and hand the turn back to them.
  const watchBargeIn = () => {
    let over = 0;
    const { on, ms } = bargeRef.current;
    const tick = () => {
      if (phaseRef.current !== "speaking") return;
      if (rms() > on) { over += 16; } else { over = Math.max(0, over - 24); }
      if (over > ms) { interrupt(); return; }
      bargeRafRef.current = requestAnimationFrame(tick);
    };
    bargeRafRef.current = requestAnimationFrame(tick);
  };

  // Stop the coach mid-sentence (barge-in or tap) and hand the mic back.
  const stopTurn = () => {
    const turn = turnRef.current;
    if (!turn) return;
    turn.cancelled = true;
    try { turn.ctrl.abort(); } catch {}
    turn.stopAudio?.();
    turn.wake?.();
  };

  const interrupt = () => {
    if (phaseRef.current !== "speaking") return;
    cancelAnimationFrame(bargeRafRef.current);
    stopTurn();
    listen();
  };

  // Bail out of a turn that is stuck waiting on the network.
  const cancelThinking = () => {
    if (phaseRef.current !== "thinking") return;
    try { abortRef.current && abortRef.current.abort(); } catch {}
    stopTurn();
    listen();
  };

  const onOrbTap = () => {
    if (phase === "error") return boot(++bootIdRef.current);
    if (phaseRef.current === "speaking") return interrupt();
    if (phaseRef.current === "thinking") return cancelThinking();
  };

  const handleEnd = () => {
    const t = turnsRef.current;
    const hasChat = t.some((x) => x.role === "user") && t.some((x) => x.role === "assistant");
    if (hasChat) {
      toast("Saving your recap...");
      fetch(`${API}/coach/recap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ turns: t.map((x) => ({ role: x.role, content: x.content })) }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (d) toast.success("Recap saved to your dashboard."); })
        .catch(() => {});
    }
    onClose();
  };

  const label = {
    connecting: "Getting the mic ready...",
    listening: "Listening. Just talk.",
    thinking: "Thinking...",
    speaking: "Coach is talking. Just talk to jump in.",
    error: "Mic needed",
  }[phase];

  const orbActive = phase === "listening" || phase === "speaking";
  const scale = phase === "listening" ? 1 + level * 0.5 : phase === "speaking" ? 1.15 : 1;

  return createPortal(
    <div className="fixed inset-0 z-[120] bg-[#0B0C0E] flex flex-col" data-testid="voice-conversation" data-first-audio-ms={latency ?? ""}>
      <div className="flex items-center justify-between px-5 pt-4" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}>
        <div className="eyebrow text-[#FD0F10]">HANDS-FREE COACH · {voice.toUpperCase()}</div>
        <button onClick={handleEnd} aria-label="End voice chat" className="w-10 h-10 rounded-full border border-[#2A2E39] flex items-center justify-center text-gray-300 hover:border-[#FD0F10] hover:text-white" data-testid="voice-end-btn">
          <X size={20} />
        </button>
      </div>

      <div className="px-5 mt-3 flex items-center gap-2" data-testid="voice-picker">
        <span className="text-xs text-gray-500 font-mono-ui uppercase tracking-wider">Voice</span>
        {VOICES.map((v) => (
          <button
            key={v.id}
            onClick={() => setVoice(v.id)}
            className={`px-3 py-1.5 rounded-full border text-xs font-mono-ui uppercase tracking-wider transition-colors ${voice === v.id ? "bg-[#FD0F10] border-[#FD0F10] text-white" : "border-[#2A2E39] text-gray-300 hover:border-[#FD0F10]"}`}
            data-testid={`voice-opt-${v.id}`}
          >
            {v.label} <span className="opacity-60">{v.sub}</span>
          </button>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 mt-4 space-y-3" data-testid="voice-transcript">
        {turns.length === 0 && phase !== "error" && (
          <p className="text-gray-400 text-sm">Say anything. When you stop talking, your coach answers out loud, then listens again. Talk over the coach any time to cut in. Tap the X to end and save a recap.</p>
        )}
        {turns.map((m, i) => (
          <div key={i} className={`p-3 rounded-lg ${m.role === "user" ? "bg-[#FD0F10]/10 border border-[#FD0F10]/30 ml-8" : "card-panel mr-6"}`}>
            <div className="eyebrow text-[#FD0F10]">{m.role === "user" ? "YOU" : "COACH"}</div>
            <div className="mt-1 text-sm text-gray-100 whitespace-pre-wrap leading-relaxed">{clean(m.content)}</div>
          </div>
        ))}
        {live && (
          <div className="p-3 rounded-lg card-panel mr-6">
            <div className="eyebrow text-[#FD0F10]">COACH</div>
            <div className="mt-1 text-sm text-gray-100 whitespace-pre-wrap leading-relaxed">{clean(live)}<span className="animate-pulse">▍</span></div>
          </div>
        )}
        {err && <p className="text-sm text-red-400" data-testid="voice-error">{err}</p>}
      </div>

      <div className="px-5 pb-8 pt-4 flex flex-col items-center gap-4" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
        {phase === "listening" && heard && (
          <div className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider text-[#FD0F10] animate-in fade-in" data-testid="voice-heard">
            <Check size={14} /> Heard you
          </div>
        )}
        <button
          onClick={onOrbTap}
          className="relative w-28 h-28 rounded-full flex items-center justify-center transition-transform"
          style={{ transform: `scale(${scale})`, transition: "transform 120ms ease-out" }}
          data-testid="voice-orb"
          aria-label={label}
        >
          {orbActive && <span className={`absolute inset-0 rounded-full ${phase === "speaking" ? "bg-[#FD0F10]/30" : "bg-[#FD0F10]/20"} animate-ping`} />}
          <span className={`relative w-full h-full rounded-full flex items-center justify-center border-2 ${phase === "listening" ? "bg-[#FD0F10] border-[#FD0F10] text-white" : phase === "speaking" ? "bg-[#FD0F10]/20 border-[#FD0F10] text-[#FD0F10]" : "bg-[#14161B] border-[#2A2E39] text-gray-300"}`}>
            {phase === "thinking" || phase === "connecting" ? <Loader2 size={34} className="animate-spin" /> : phase === "speaking" ? <Volume2 size={34} /> : <Mic size={34} />}
          </span>
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold text-gray-100" data-testid="voice-status">{label}</div>
          <button onClick={handleEnd} className="mt-3 text-xs text-gray-400 underline hover:text-white" data-testid="voice-end-link">End and save recap</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
